// React Imports
import React from 'react'

// Helmet
import {Helmet} from 'react-helmet'

// Proptypes
import PropTypes from 'prop-types'

/**
 * MetaTagsComponent is a React functional component responsible for setting
 * the meta tags of a web page, including the title, description, and keywords.
 *
 * This component utilizes the `react-helmet` library to dynamically update
 * the document head with the appropriate meta tags based on the provided
 * metadata and search query. It attempts to retrieve the values for the
 * title, description, and keywords from the `metaData` array using their
 * unique identifiers. If a value is not found, it defaults to using the
 * `searchQuery` for the title and description, and an empty string for
 * keywords.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.searchQuery - The search query string used as a fallback
 * for the title and description if specific meta tags are not found.
 * @param {Array<Object>} props.metaData - An array of metadata objects, each
 * containing an `id` and `value` property, used to populate the meta tags.
 *
 * @returns {JSX.Element} A JSX element that updates the document's head
 * with the specified meta tags.
 */
const MetaTagsComponent = ({searchQuery, metaData}) => {
    /**
     * Retrieves the value of a meta tag by its unique identifier.
     *
     * This function searches through a collection of metadata items to find
     * a meta tag that matches the provided identifier. If a matching meta tag
     * is found, its value is returned. If no match is found, an empty string
     * is returned.
     *
     * @param {string} id - The unique identifier of the meta tag to retrieve.
     * @returns {string} The value of the meta tag if found, otherwise an empty string.
     */
    const getMetaValue = (id) => {
        if (!metaData) return null
        const meta = metaData.find((item) => item.id == id)
        return meta ? meta.value : ''
    }

    const pageTitle = getMetaValue('title') || searchQuery
    const pageDescription = getMetaValue('description') || searchQuery
    const pageKeywords = getMetaValue('keywords') || ''
    const robotsContent = getMetaValue('robots')

    return (
        <Helmet>
            {pageTitle && <title>{pageTitle}</title>}
            {pageDescription && <meta name="description" content={pageDescription} />}
            {pageKeywords && <meta name="keywords" content={pageKeywords} />}
            {robotsContent && <meta name="robots" content={robotsContent} />}
        </Helmet>
    )
}

export default MetaTagsComponent

MetaTagsComponent.propTypes = {
    searchQuery: PropTypes.object,
    metaData: PropTypes.array
}
